import { cn } from "@/shared/lib/cn";
import { messages } from "@/shared/i18n";

/** 0 = no input, 1 = weak, 2 = medium, 3 = strong, 4 = very strong */
type PasswordStrengthLevel = 0 | 1 | 2 | 3 | 4;

const STRENGTH_CONFIG: Record<
  Exclude<PasswordStrengthLevel, 0>,
  { label: keyof typeof messages.components.passwordStrength; barColor: string; textColor: string }
> = {
  1: { label: "weak", barColor: "bg-red-500", textColor: "text-red-600" },
  2: { label: "medium", barColor: "bg-amber-500", textColor: "text-amber-600" },
  3: { label: "strong", barColor: "bg-lime-500", textColor: "text-lime-600" },
  4: { label: "veryStrong", barColor: "bg-green-600", textColor: "text-green-700" },
};

/**
 * Scores password strength by length and character variety (lower, upper, digit, special).
 * Follows NIST guidance: length matters most, variety adds entropy.
 */
function getPasswordStrength(value: string): PasswordStrengthLevel {
  if (!value.length) return 0;

  const len = value.length;
  const hasLower = /[a-z]/.test(value);
  const hasUpper = /[A-Z]/.test(value);
  const hasNumber = /\d/.test(value);
  const hasSpecial = /[^a-zA-Z0-9]/.test(value);
  const varietyCount = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;

  if (len < 8) return 1;
  if (len >= 12 && varietyCount >= 4) return 4;
  if (len >= 16 && varietyCount >= 3) return 4;
  if (len >= 8 && varietyCount >= 3) return 3;
  if (len >= 12 && varietyCount >= 2) return 3;
  if (len >= 8 && varietyCount >= 2) return 2;
  if (len >= 12 && varietyCount >= 1) return 2;
  return 1;
}

interface PasswordStrengthIndicatorProps {
  /** The password being typed (first field only — not the confirm field). */
  value: string;
  className?: string;
  "data-testid"?: string;
}

/**
 * Displays a colored strength bar and label below a password input.
 * Use only on "new password" fields (registration, reset) — not on login.
 */
export function PasswordStrengthIndicator({
  value,
  className,
  "data-testid": dataTestId,
}: PasswordStrengthIndicatorProps) {
  const level = getPasswordStrength(value);

  if (level === 0) {
    return (
      <div
        aria-hidden
        className={cn("w-full", className)}
        data-testid={dataTestId ? `${dataTestId}-strength` : undefined}
      >
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-gray-200" />
      </div>
    );
  }

  const config = STRENGTH_CONFIG[level];
  const label = messages.components.passwordStrength[config.label];
  const fillPercent = (level / 4) * 100;

  return (
    <div
      aria-label={label}
      aria-live="polite"
      className={cn("w-full", className)}
      data-testid={dataTestId ? `${dataTestId}-strength` : undefined}
      role="status"
    >
      <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-gray-200">
        <div
          className={cn("h-full rounded-full transition-all duration-300", config.barColor)}
          data-strength-level={level}
          style={{ width: `${String(fillPercent)}%` }}
        />
      </div>
      <p
        className={cn("mt-1 text-sm font-medium", config.textColor)}
        data-testid={dataTestId ? `${dataTestId}-strength-label` : undefined}
      >
        {label}
      </p>
    </div>
  );
}

PasswordStrengthIndicator.displayName = "PasswordStrengthIndicator";
